/**
 * Drizzle query builders + query keys de COHORTES y PROGRAMAS (F2-A).
 *
 * Consumo local-first sobre los streams por rol: my_cohorts_student /
 * my_cohorts_teacher / campus_admin_scope / org_admin_scope / academy_org
 * (programas y cohortes abiertas). La escritura pasa por el write-path o los
 * endpoints REST de /api/cohorts (activación/cupo/waitlist server-side).
 */

import {
  and,
  asc,
  cohort,
  cohort_enrollment,
  cohort_group,
  cohort_group_member,
  cohort_module_setting,
  desc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  program,
  program_course,
} from "@space/powersync";
import type { components } from "@space/client";

export const cohortQueryKeys = {
  all: ["academy-cohorts"] as const,
  byOrg: (orgId: string | undefined) => ["academy-cohorts", "org", orgId] as const,
  byId: (cohortId: string | undefined) => ["academy-cohorts", "detail", cohortId] as const,
  openByCourse: (orgId: string | undefined, courseId: string | undefined) =>
    ["academy-cohorts", "open", orgId, courseId] as const,
  myEnrollments: (userId: string | undefined) =>
    ["academy-cohorts", "my-enrollments", userId] as const,
  // Nav por rol: ¿tengo alguna fila cohort_teacher propia? (docente asignado).
  myTeaching: (userId: string | undefined) => ["academy-cohorts", "my-teaching", userId] as const,
  roster: (cohortId: string | undefined) => ["academy-cohorts", "roster", cohortId] as const,
  /** 1C: fichas PÚBLICAS de los profesores de una cohorte (portada del aula). */
  teacherProfiles: (cohortId: string | undefined) =>
    ["academy-cohorts", "teacher-profiles", cohortId] as const,
  /**
   * Plan 06 · E3: identidades de la gente de la cohorte. Fuente ÚNICA y única
   * posible — el espejo local de `"user"` desapareció con el recorte de los
   * streams, así que ya no hay dos orígenes que separar por `mode`.
   */
  identities: (cohortId: string | undefined) =>
    ["academy-cohorts", "identities", cohortId] as const,
  groups: (cohortId: string | undefined) => ["academy-cohorts", "groups", cohortId] as const,
  groupMembers: (cohortId: string | undefined) =>
    ["academy-cohorts", "group-members", cohortId] as const,
  moduleSettings: (cohortId: string | undefined) =>
    ["academy-cohorts", "module-settings", cohortId] as const,
  /**
   * Catálogo interno de la academia. Plan 06 · E1: hay UNA sola fuente,
   * `GET /api/academy/:orgId/courses`. La key `orgCourses` (espejo local sobre
   * `course_organization`) desapareció con el stream `org_courses`.
   */
  orgCoursesRest: (orgId: string | undefined) =>
    ["academy-cohorts", "org-courses-rest", orgId] as const,
  programs: (orgId: string | undefined) => ["academy-programs", "org", orgId] as const,
  programById: (programId: string | undefined) =>
    ["academy-programs", "detail", programId] as const,
  programCourses: (programId: string | undefined) =>
    ["academy-programs", "courses", programId] as const,
};

// ─── Cohortes ────────────────────────────────────────────────────────────────

/** Cohortes de una organización (vista admin/teacher — lo que el stream sincronizó). */
export function getCohortsByOrgQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(cohort)
    .where(and(eq(cohort.organization_id, orgId), isNull(cohort.deleted_at)))
    .orderBy(desc(cohort.created_at));
}

/** Una cohorte por id. */
export function getCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db.select().from(cohort).where(eq(cohort.id, cohortId)).limit(1);
}

/** Cohortes ABIERTAS de un curso en mi org (selección de matrícula en catálogo). */
export function getOpenCohortsByCourseQuery(
  db: PowerSyncDrizzleDB,
  orgId: string,
  courseId: string,
) {
  return db
    .select()
    .from(cohort)
    .where(
      and(
        eq(cohort.organization_id, orgId),
        eq(cohort.course_id, courseId),
        eq(cohort.status, "open"),
        isNull(cohort.deleted_at),
      ),
    )
    .orderBy(asc(cohort.start_date));
}

/** MIS matrículas de cohorte (privadas, stream my_cohorts_student). */
export function getMyCohortEnrollmentsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(cohort_enrollment)
    .where(and(eq(cohort_enrollment.user_id, userId), isNull(cohort_enrollment.deleted_at)))
    .orderBy(desc(cohort_enrollment.created_at));
}

/**
 * Roster de una cohorte: **solo la matrícula**.
 *
 * **D3 (plan 06 · E3):** el `leftJoin(user, …)` que traía nombre/correo/imagen
 * se ha BORRADO. La identidad tiene una sola fuente en el cliente
 * (`useCohortIdentities`, que la pide a `GET /api/cohorts/:id/roster`, el único
 * sitio donde el correo se gatea por capacidad) y se pega con `attachIdentity`.
 * Las columnas de `cohort_enrollment` se quedan: siguen llegando por sync
 * porque el profesor las escribe sin conexión en el aula.
 */
export function getCohortRosterQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select({
      id: cohort_enrollment.id,
      user_id: cohort_enrollment.user_id,
      status: cohort_enrollment.status,
      waitlist_position: cohort_enrollment.waitlist_position,
      enrolled_at: cohort_enrollment.enrolled_at,
      completed_at: cohort_enrollment.completed_at,
      withdrawn_at: cohort_enrollment.withdrawn_at,
      final_grade: cohort_enrollment.final_grade,
      notes: cohort_enrollment.notes,
    })
    .from(cohort_enrollment)
    .where(and(eq(cohort_enrollment.cohort_id, cohortId), isNull(cohort_enrollment.deleted_at)))
    .orderBy(asc(cohort_enrollment.created_at));
}

// E4 (plan 06) · BORRADA: aquí vivía `getCohortTeacherProfilesQuery`, el
// `innerJoin(cohort_teacher, teacher_profile)` que pintaba la portada del aula.
// Dependía de que `academy_org` replicara TODAS las fichas públicas de la
// academia a TODO miembro para mostrar las dos o tres de un aula — el mismo
// patrón de «replicar mil para enseñar tres» que E4 cierra. Lo sustituye
// `GET /api/cohorts/:id/teachers`, que devuelve exactamente las de esa cohorte
// y sigue sin tocar la identidad de la persona (nombre y correo son del roster,
// que no responde a un alumno).

/**
 * Ficha pública de un profesor de la cohorte (sin identidad de la persona).
 *
 * E4: se DERIVA del DTO del servidor en vez de declararse a mano. El cambio no
 * es cosmético — `specialties` era `string | null` porque SQLite guardaba el
 * `jsonb` serializado y cada consumidor lo re-parseaba; ahora llega ya como
 * array y el parseo desaparece.
 */
export type CohortTeacherProfileRow = components["schemas"]["CohortTeacherCardDTO"];

/** Fila del roster SIN identidad (lo que sale de la consulta local). */
export interface CohortRosterRow {
  id: string;
  user_id: string | null;
  status: string | null;
  waitlist_position: number | null;
  enrolled_at: string | null;
  completed_at: string | null;
  withdrawn_at: string | null;
  final_grade: number | null;
  notes: string | null;
}

// D3 (plan 06 · E3): `getCohortTeachersQuery` y `CohortTeacherRow` se han
// BORRADO. Eran el segundo join de identidad —sobre `cohort_teacher`— y su
// hook `useCohortTeachers` no tenía NI UN consumidor: ninguna pantalla pinta
// hoy la lista de profesores de la cohorte. Se borra entera en vez de
// migrarla; mantener viva una consulta que nadie llama es justo la deuda que
// la política prohíbe. Cuando una pantalla la necesite, los profesores de la
// cohorte ya vienen en `GET /api/cohorts/:id/roster`.

/** Grupos activos de una cohorte. */
export function getCohortGroupsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_group)
    .where(and(eq(cohort_group.cohort_id, cohortId), isNull(cohort_group.deleted_at)))
    .orderBy(asc(cohort_group.name));
}

/** Miembros de todos los grupos de una cohorte. */
export function getCohortGroupMembersQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_group_member)
    .where(eq(cohort_group_member.cohort_id, cohortId))
    .orderBy(asc(cohort_group_member.created_at));
}

/** Ajustes por módulo (due/open/visibilidad) de una cohorte. */
export function getCohortModuleSettingsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_module_setting)
    .where(eq(cohort_module_setting.cohort_id, cohortId));
}

// ─── Cursos de la organización (picker de cohortes/programas) ────────────────
//
// D2 · BORRADO: aquí vivían `getOrgCoursesQuery` (join local sobre
// `course_organization`) y su fila `OrgCourseRow`. Leían la réplica que
// alimentaba el stream `org_courses`, hoy eliminado: la tabla ya no viaja al
// cliente, así que la consulta devolvería siempre vacío.
//
// El catálogo interno de la academia se pide a `GET /api/academy/:orgId/courses`
// (`fetchOrgCourses`), que filtra publicación y borrado y exige
// `ManageAcademics` para los borradores. La forma de fila la declara ahora el
// único consumidor (`useOrgCourses`), derivada del DTO generado — no una
// interfaz a mano que imite la consulta muerta.

// ─── Programas ───────────────────────────────────────────────────────────────

/** Programas activos de una organización. */
export function getProgramsByOrgQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(program)
    .where(and(eq(program.organization_id, orgId), isNull(program.deleted_at)))
    .orderBy(asc(program.name));
}

/** Un programa por id. */
export function getProgramQuery(db: PowerSyncDrizzleDB, programId: string) {
  return db.select().from(program).where(eq(program.id, programId)).limit(1);
}

/** Cursos de un programa, ordenados. */
export function getProgramCoursesQuery(db: PowerSyncDrizzleDB, programId: string) {
  return db
    .select()
    .from(program_course)
    .where(eq(program_course.program_id, programId))
    .orderBy(asc(program_course.position));
}
